import * as React from 'react'
import {
  AlertCircle,
  ArrowRight,
  ClipboardPaste,
  MessageSquareDashed,
  Sparkles,
  TriangleAlert,
} from 'lucide-react'
import type { AutofillResult } from '@acs/shared'
import { AUTOFILL_MAX_CHARS } from '@acs/shared'
import { ApiError } from '@/lib/api'
import { cn } from '@/lib/cn'
import {
  AUTOFILL_LABEL,
  findConflicts,
  type ApplyMode,
  type AutofillValues,
  type Conflict,
} from '@/lib/autofill'
import { Button } from '@/components/ui/button'
import { Dialog } from '@/components/ui/dialog'
import { Kbd } from '@/components/ui/kbd'
import { Tooltip } from '@/components/ui/tooltip'
import { useAutofill } from '@/hooks/use-create-customer'

/**
 * "Fill from message" — paste a WhatsApp message, the form fills itself.
 *
 * Designed around the operator's fastest path: the box is focused the moment
 * the dialog opens, and PASTING is the submit — a paste into an empty box
 * starts the read at once, no button. The button is there for a message typed
 * or edited by hand.
 *
 * The dialog never writes to the form itself. It reads, then hands the result
 * to the page through `onApply`, and closes only when the page has taken it.
 * The one time it stays open with a result is a conflict — the message
 * disagrees with something the operator already typed — because that is a
 * decision, not a fill.
 */

/* ------------------------------------------------------------------ *
 * The trigger
 * ------------------------------------------------------------------ */

const IS_MAC =
  typeof navigator !== 'undefined' && /Mac|iPhone|iPad|iPod/.test(navigator.platform ?? '')

/** The chord that opens the dialog from anywhere on the page, as keycaps. */
export const AUTOFILL_SHORTCUT: readonly string[] = IS_MAC ? ['⌘', '⇧', 'V'] : ['Ctrl', 'Shift', 'V']

function Shortcut() {
  return (
    <span className="inline-flex items-center gap-0.5 align-middle">
      {AUTOFILL_SHORTCUT.map((key) => (
        <Kbd key={key}>{key}</Kbd>
      ))}
    </span>
  )
}

/**
 * The header button. Rendered disabled, with the reason, when the server has
 * no OpenAI key — a feature that vanishes is a support question; one that says
 * why it is off is not.
 */
export function AutofillButton(p: { available: boolean; onClick: () => void }) {
  return (
    <Tooltip
      side="bottom"
      content={
        p.available ? (
          <span>
            Paste a WhatsApp message and the form fills itself. Or paste anywhere on this page, or
            press <Shortcut />.
          </span>
        ) : (
          'Not set up on this server — OPENAI_API_KEY is missing.'
        )
      }
    >
      {/* A disabled button is not a hover target; the span is what the
          tooltip attaches to. */}
      <span className="inline-block">
        <Button
          type="button"
          variant="secondary"
          size="sm"
          disabled={!p.available}
          onClick={p.onClick}
          className={cn(p.available && 'cta-sheen border-accent/40 text-accent hover:bg-accent-soft')}
        >
          <Sparkles aria-hidden="true" />
          Fill from message
        </Button>
      </span>
    </Tooltip>
  )
}

/* ------------------------------------------------------------------ *
 * The dialog
 * ------------------------------------------------------------------ */

type Phase =
  | { kind: 'idle' }
  | { kind: 'reading' }
  | { kind: 'review'; result: AutofillResult; conflicts: Conflict[] }
  | { kind: 'empty'; result: AutofillResult }
  | { kind: 'error'; message: string }

/** What the operator reads while the model works — activity, not a spinner. */
const STEPS = [
  'Reading the message…',
  'Picking out names, numbers and dates…',
  'Sorting the address and the secrets…',
  'Checking every value…',
] as const

const PLACEHOLDER =
  'Name: Priya Sharma\nMobile: 647 555 0142\nEmail: priya.s@gmail.com\nPIN 482913 · App password: abcd efgh ijkl mnop\nSIN 046 454 286 · DOB 14/03/1996 · Female\n12 Main St Unit 4, Brampton ON L6Y 1J7\nFull time, day shift'

const TEXTAREA =
  'block w-full min-h-[13rem] resize-y rounded-md border bg-s1 px-3 py-2 font-mono text-[13px] leading-relaxed text-fg outline-none ' +
  'transition-colors duration-300 placeholder:text-faint hover:border-line-strong ' +
  'focus:border-accent focus:ring-2 focus:ring-accent/25 read-only:cursor-default'

export interface AutofillDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /**
   * Text that arrived with the open — a paste anywhere on the page. Read
   * immediately, so the paste itself is the whole interaction.
   */
  seedText: string | null
  /** The form's values right now, for spotting conflicts. */
  currentValues: () => AutofillValues
  onApply: (result: AutofillResult, mode: ApplyMode) => void
}

export function AutofillDialog(p: AutofillDialogProps) {
  const { open, onOpenChange, seedText, currentValues, onApply } = p
  const read = useAutofill()
  const [text, setText] = React.useState('')
  const [phase, setPhase] = React.useState<Phase>({ kind: 'idle' })
  const [step, setStep] = React.useState(0)
  const aborter = React.useRef<AbortController | null>(null)
  const textareaRef = React.useRef<HTMLTextAreaElement>(null)

  const reading = phase.kind === 'reading'

  const cancel = React.useCallback(() => {
    aborter.current?.abort()
    aborter.current = null
  }, [])

  const focusBox = React.useCallback(() => {
    // Deferred a frame: Radix's focus scope puts focus on the first control
    // (the close button) as the dialog mounts, and this has to run after.
    const id = requestAnimationFrame(() => textareaRef.current?.focus())
    return () => cancelAnimationFrame(id)
  }, [])

  /** "Stop": abandon the read and hand the box back, text intact. */
  const stop = React.useCallback(() => {
    cancel()
    setPhase({ kind: 'idle' })
    focusBox()
  }, [cancel, focusBox])

  const run = React.useCallback(
    (raw: string) => {
      const value = raw.trim()
      if (value === '' || value.length > AUTOFILL_MAX_CHARS) return
      cancel()
      const controller = new AbortController()
      aborter.current = controller
      setPhase({ kind: 'reading' })
      setStep(0)

      read.mutate(
        { text: value, signal: controller.signal },
        {
          onSuccess: (result) => {
            if (controller.signal.aborted) return
            aborter.current = null
            if (Object.keys(result.fields).length === 0) {
              setPhase({ kind: 'empty', result })
              return
            }
            const conflicts = findConflicts(currentValues(), result.fields)
            if (conflicts.length > 0) {
              setPhase({ kind: 'review', result, conflicts })
              return
            }
            // The common case: an empty form. Nothing to ask — fill it.
            setPhase({ kind: 'idle' })
            setText('')
            onApply(result, 'replace')
          },
          onError: (err) => {
            // A cancelled read is not an error the operator needs to see.
            if (controller.signal.aborted) return
            aborter.current = null
            setPhase({
              kind: 'error',
              message:
                err instanceof ApiError
                  ? err.message
                  : 'Could not reach the server. Check the connection and try again.',
            })
          },
        }
      )
    },
    [cancel, currentValues, onApply, read]
  )

  /*
   * A page-level paste opens the dialog already reading; a plain open lands
   * the cursor in the box so ⌘V is the very next keystroke.
   *
   * Keyed on `open` alone, deliberately: the seed is consumed at open time,
   * and `run` changing identity later must not start a second read.
   */
  React.useEffect(() => {
    if (!open) return
    if (seedText !== null && seedText.trim() !== '') {
      setText(seedText)
      run(seedText)
    }
    return focusBox()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  // Closing abandons an in-flight read (the server stops its model call too)
  // and drops any result: a conflict list computed against the form as it was
  // would be stale by the next open. The text is kept for a re-open.
  React.useEffect(() => {
    if (open) return
    cancel()
    setPhase({ kind: 'idle' })
  }, [open, cancel])

  React.useEffect(() => cancel, [cancel])

  // Walk the status line forward while reading, never past the last step.
  React.useEffect(() => {
    if (!reading) return
    const id = window.setInterval(() => setStep((s) => Math.min(s + 1, STEPS.length - 1)), 900)
    return () => window.clearInterval(id)
  }, [reading])

  const apply = (result: AutofillResult, mode: ApplyMode): void => {
    setPhase({ kind: 'idle' })
    setText('')
    onApply(result, mode)
  }

  const backToEditing = (): void => {
    setPhase({ kind: 'idle' })
    focusBox()
  }

  /** A paste into an EMPTY box is the submit. Into a non-empty one, it just pastes. */
  const onPaste = (e: React.ClipboardEvent<HTMLTextAreaElement>): void => {
    if (reading || text.trim() !== '') return
    const pasted = e.clipboardData.getData('text')
    if (pasted.trim() === '') return
    e.preventDefault()
    setText(pasted)
    run(pasted)
  }

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>): void => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault()
      run(text)
    }
  }

  const tooLong = text.length > AUTOFILL_MAX_CHARS
  const nearLimit = text.length > AUTOFILL_MAX_CHARS * 0.7
  const canRun = !reading && text.trim() !== '' && !tooLong

  const showEditor = phase.kind === 'idle' || phase.kind === 'reading' || phase.kind === 'error'

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Fill from message"
      description="Paste what the customer sent — WhatsApp, SMS, a note. Only the fields it contains are filled; cities and warehouses stay yours to pick. Nothing is submitted."
      widthClass="w-[min(600px,calc(100vw-1.5rem))]"
      footer={
        phase.kind === 'review' ? (
          <>
            <Button type="button" variant="ghost" onClick={backToEditing}>
              Edit message
            </Button>
            <Button type="button" variant="secondary" onClick={() => apply(phase.result, 'fill-empty')}>
              Keep what I typed
            </Button>
            <Button type="button" variant="primary" onClick={() => apply(phase.result, 'replace')}>
              <Sparkles aria-hidden="true" />
              Replace with the message
            </Button>
          </>
        ) : phase.kind === 'empty' ? (
          <>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Close
            </Button>
            <Button type="button" variant="primary" onClick={backToEditing}>
              Edit the message
            </Button>
          </>
        ) : (
          <>
            <Button
              type="button"
              variant="ghost"
              onClick={() => (reading ? stop() : onOpenChange(false))}
            >
              {reading ? 'Stop' : 'Cancel'}
            </Button>
            <Button
              type="button"
              variant="primary"
              disabled={!canRun}
              loading={reading}
              onClick={() => run(text)}
            >
              {reading ? null : <Sparkles aria-hidden="true" />}
              {reading ? 'Reading…' : 'Fill the form'}
            </Button>
          </>
        )
      }
    >
      {showEditor ? (
        <div key="editor" className="panel-in space-y-3 py-1">
          <div className="relative overflow-hidden rounded-md">
            <textarea
              ref={textareaRef}
              value={text}
              onChange={(e) => {
                setText(e.target.value)
                if (phase.kind === 'error') setPhase({ kind: 'idle' })
              }}
              onPaste={onPaste}
              onKeyDown={onKeyDown}
              readOnly={reading}
              spellCheck={false}
              rows={9}
              aria-label="Message to read"
              aria-invalid={tooLong || undefined}
              placeholder={PLACEHOLDER}
              className={cn(
                TEXTAREA,
                tooLong ? 'border-danger focus:border-danger focus:ring-danger/25' : 'border-line',
                reading && 'text-faint'
              )}
            />
            {/* The read, made visible: a soft band sweeping down the text. */}
            {reading ? (
              <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-0 overflow-hidden rounded-md"
              >
                <div className="anim-scan absolute inset-x-0 h-2/5 bg-[linear-gradient(to_bottom,transparent,var(--accent-soft),transparent)]" />
              </div>
            ) : null}
          </div>

          <div className="flex min-h-5 items-center justify-between gap-3 text-[12px]">
            {reading ? (
              <p
                key={`step-${step}`}
                role="status"
                className="panel-in flex items-center gap-1.5 font-medium text-accent"
              >
                <Sparkles aria-hidden="true" className="size-3.5 animate-pulse" />
                {STEPS[step]}
              </p>
            ) : (
              <p className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-faint">
                <ClipboardPaste aria-hidden="true" className="size-3.5 flex-none" />
                <span>
                  Pasting starts the read on its own. <Kbd>{IS_MAC ? '⌘' : 'Ctrl'}</Kbd>{' '}
                  <Kbd>Enter</Kbd> also works.
                </span>
              </p>
            )}
            {nearLimit || tooLong ? (
              <span
                className={cn('tabular shrink-0', tooLong ? 'font-semibold text-danger' : 'text-faint')}
              >
                {text.length.toLocaleString()} / {AUTOFILL_MAX_CHARS.toLocaleString()}
              </span>
            ) : null}
          </div>

          {phase.kind === 'error' ? (
            <div
              role="alert"
              className="panel-in flex items-start gap-2 rounded-md border border-danger/30 bg-danger-soft px-3 py-2.5 text-[13px] text-danger"
            >
              <AlertCircle aria-hidden="true" className="mt-0.5 size-4 flex-none" />
              <span>{phase.message}</span>
            </div>
          ) : null}
        </div>
      ) : phase.kind === 'review' ? (
        <div key="review" className="panel-in py-1">
          <ReviewPanel result={phase.result} conflicts={phase.conflicts} />
        </div>
      ) : (
        <div key="empty" className="panel-in py-1">
          <EmptyPanel result={phase.result} />
        </div>
      )}
    </Dialog>
  )
}

/* ------------------------------------------------------------------ *
 * Result panels
 * ------------------------------------------------------------------ */

function ReviewPanel(p: { result: AutofillResult; conflicts: Conflict[] }) {
  const others = Object.keys(p.result.fields).length - p.conflicts.length

  return (
    <div className="space-y-3">
      <div className="flex items-start gap-2.5 rounded-md border border-warn/30 bg-warn-soft px-3 py-2.5">
        <TriangleAlert aria-hidden="true" className="mt-0.5 size-4 flex-none text-warn" />
        <p className="text-[13px] text-fg">
          The message disagrees with{' '}
          <span className="font-semibold">
            {p.conflicts.length} {p.conflicts.length === 1 ? 'field' : 'fields'}
          </span>{' '}
          you already filled in. Keep yours, or take the message’s?
        </p>
      </div>

      <ul className="divide-y divide-line overflow-hidden rounded-md border border-line bg-s1">
        {p.conflicts.map((c) => (
          <li
            key={c.field}
            className="grid grid-cols-[7rem_1fr] items-start gap-x-3 px-3 py-2.5 text-[13px] sm:grid-cols-[8rem_1fr]"
          >
            <span className="pt-px text-[11px] font-semibold tracking-[0.05em] text-muted uppercase">
              {AUTOFILL_LABEL[c.field]}
            </span>
            <span className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5">
              <span className="truncate text-muted line-through decoration-line-strong">
                {c.current}
              </span>
              <ArrowRight aria-hidden="true" className="size-3.5 flex-none text-faint" />
              <span className="truncate font-medium text-fg">{c.next}</span>
            </span>
          </li>
        ))}
      </ul>

      {others > 0 ? (
        <p className="text-[12px] text-faint">
          {others} other {others === 1 ? 'field fills' : 'fields fill'} either way.
        </p>
      ) : null}
    </div>
  )
}

function EmptyPanel(p: { result: AutofillResult }) {
  const notes = p.result.skipped.map((s) => s.reason)
  return (
    <div className="flex flex-col items-center px-2 py-6 text-center">
      <div className="mb-3 grid size-11 place-items-center rounded-xl bg-s3 text-faint">
        <MessageSquareDashed aria-hidden="true" className="size-5" />
      </div>
      <p className="text-[13.5px] font-semibold text-fg">Nothing to fill</p>
      <p className="mt-1 max-w-sm text-[13px] text-muted">
        The message didn’t seem to contain any of the form’s details. Check it has things like a
        name, phone, email, SIN, date of birth or an address.
      </p>
      {notes.length > 0 ? (
        <ul className="mt-4 w-full space-y-1 text-left text-[12px] text-warn">
          {notes.map((n) => (
            <li key={n} className="flex items-start gap-1.5">
              <TriangleAlert aria-hidden="true" className="mt-px size-3.5 flex-none" />
              {n}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}
